/**
 * R32 X1/X2 — the S7 Done-as-SUBMIT decision + the PERSISTENT Saved ack state (extends R31's
 * Done-as-commit; the D2 wire client half).
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * R31 made Done a COMMIT: validate → inline ack tick → blur → advance. But the tick was PURELY LOCAL
 * and TRANSIENT (a 900ms `.done-acked` flash), and Done did NOT put the value on the SERVER — it only
 * blurred the field. The dev-119 Hindi close proved that gap is a Key-Rule-6 failure: the visitor
 * typed her email, tapped Done, saw "Saved ✓" WHICH THEN REVERTED to Done, and `leads.email` stayed
 * EMPTY (Done was blur-only; the R30 draft signal deliberately carries no committed value). She then
 * voice-announced "done" — but there was nothing on file, `capture_email` captured nothing, no
 * `send_spec_sheet` fired, and the close hung.
 *
 * R32 closes it on the CLIENT side:
 *   X1 — Done on a VALID email SUBMITS the value to the server (via the existing `submit-contact`
 *        wire — the concierge R58 D2 "freeze the existing contact-submit path" seam) so `leads.email`
 *        becomes real and the mechanical send (R58 D1) has an address to send to. Contact-ONLY: Done
 *        does NOT trigger the full "on its way" send screen (that's the kiosk Send pill's / the
 *        server's mechanical-send job); Done just gets the value on file.
 *   X2 — the ack is a PERSISTENT "Saved" state that HOLDS until the field VALUE CHANGES. A kiosk
 *        visitor reads a reverting tick as "it didn't take" (the field truth). The state is truthful
 *        to the server: it shows a PENDING shade while the submit is in-flight and flips to SAVED only
 *        on the server `ui.contact_ack` (or an error state on a rejected submit).
 *
 * This module is the PURE decision + state layer (no React / no DOM) so both halves are unit-testable
 * without jsdom (the kiosk vitest suite has none — the same discipline as r31/r30). The CloseScene
 * host carries the DOM side-effects (blur/focus/scroll) + renders the state this module computes.
 *
 * ── The wire (D2) ──────────────────────────────────────────────────────────────────────────────
 * BINDING STATUS (UNVERIFIED — cross-verify at integration): the concierge R58 D2 half (the server
 * that lands `submit-contact {email, phone?}` into `leads.email` and acks so Saved is truthful) is
 * branch-parallel and NOT landed at R32 authoring time. R32 builds behind the DOCUMENTED shape: Done
 * reuses the EXISTING `submit-contact` client message (already emitted by `sendContactForm` →
 * `useVoice.sendContactForm`; server acks `ui.contact_ack {persisted}` per voice-client-contract §6)
 * — the R58 D2 handoff explicitly says "if the kiosk's existing contact-submit path can carry a
 * contact-only submit, freeze that". So the Done-submit rides the same wire the Send button does; the
 * ONLY new client behaviour is emitting it from Done (contact-only, no send-screen) + reading the ack
 * into a persistent state. If R58 instead freezes a distinct `{type:'contact-submit'}` message, the
 * ONE call site (`onSubmitEmail`/`sendContactForm`) rebinds with no change here (the R26/R30 lesson —
 * a key diff IS the bug; the seam is one function).
 */

import { isValidEmailShape } from './contactCommit';

/**
 * The persistent per-field submit-ack state (X2). Distinct from R31's transient `.done-acked` flash:
 *   - 'idle'    → the field has never been submitted at THIS value (show the "Done" affordance).
 *   - 'pending' → a submit for the CURRENT value is in-flight (awaiting the server ack) — a soft
 *                 "Saving…" shade so the visitor sees the submit is working, not stalled.
 *   - 'saved'   → the server acked the CURRENT value persisted (truthful "Saved ✓" that HOLDS).
 *   - 'error'   → the server rejected the submit (a gentle "couldn't save — tap to retry", never
 *                 silence and never a false Saved).
 * The state is keyed to the SUBMITTED VALUE: any edit that changes the value away from the submitted
 * one drops back to 'idle' (the ack is no longer truthful for what's now in the box).
 */
export type SubmitAckState = 'idle' | 'pending' | 'saved' | 'error';

/**
 * The per-field submit record the host holds (one for email, one for phone). `state` is the live ack
 * state; `submittedValue` is the exact (trimmed) value the state describes — the truth anchor for X2.
 * When the field's current value ≠ `submittedValue`, the ack is stale and the host reverts to 'idle'.
 */
export type FieldSubmit = {
  state: SubmitAckState;
  /** The trimmed value the ack describes (empty until a first submit). */
  submittedValue: string;
};

/** The initial (never-submitted) record. */
export const IDLE_SUBMIT: FieldSubmit = { state: 'idle', submittedValue: '' };

/**
 * What a Done-as-SUBMIT should DO (the X1 decision — the pure verb the host maps to side-effects).
 *   - 'submit'      → the email is a valid NEW value → POST it (contact-only) + go 'pending' + blur +
 *                      advance (surface the Send pill / phone per the R31 advance).
 *   - 'resubmit'    → the email is valid but UNCHANGED from a prior submit → re-ack WITHOUT re-posting
 *                      (client-side idempotency — the value is already on file; just re-affirm Saved).
 *                      Matches the R58 D1 idempotency (one send per session; a duplicate is a no-op).
 *   - 'hint'        → validation failed → the R31 inline hint, hold focus, do NOT submit or advance.
 *   - 'skip'        → an OPTIONAL phone with an empty value → not an error, nothing to submit; the host
 *                      advances (the R31 phone semantics) WITHOUT a network post.
 */
export type SubmitAction = 'submit' | 'resubmit' | 'hint' | 'skip';

export type SubmitDecision = {
  action: SubmitAction;
  /** The advance intent (reuses the R31 vocabulary), meaningful for submit/resubmit/skip; '' on hint. */
  advance: 'focus-phone' | 'surface-send' | 'send' | 'none' | '';
  /** The inline hint text when `action === 'hint'` (empty otherwise). Never silence. */
  hint: string;
};

/**
 * Decide a Done-as-SUBMIT on the EMAIL field (X1). Valid + changed → 'submit'; valid + same as the
 * last submitted value → 'resubmit' (idempotent re-ack, no re-post); invalid → 'hint' (unchanged
 * from R31). The advance mirrors R31: phone empty → focus-phone; phone filled → surface-send.
 *
 * @param value          the current email value (verbatim; trimmed inside).
 * @param opts.phoneFilled  is the phone field non-empty? (advance target).
 * @param opts.lastSubmitted the value the CURRENT email ack describes (from the FieldSubmit record);
 *                           an equal (trimmed) value → idempotent 'resubmit'.
 */
export function decideEmailSubmit(
  value: string,
  opts: { phoneFilled: boolean; lastSubmitted: string }
): SubmitDecision {
  const v = value.trim();
  if (v === '') {
    return { action: 'hint', advance: '', hint: 'Add your email so I can send your spec sheet.' };
  }
  if (!isValidEmailShape(v)) {
    return {
      action: 'hint',
      advance: '',
      hint: "That doesn't look like an email yet — check for a typo?",
    };
  }
  const advance: SubmitDecision['advance'] = opts.phoneFilled ? 'surface-send' : 'focus-phone';
  // Idempotency (X1): a Done on the SAME already-submitted value re-acks without re-posting.
  if (v === opts.lastSubmitted.trim() && opts.lastSubmitted.trim() !== '') {
    return { action: 'resubmit', advance, hint: '' };
  }
  return { action: 'submit', advance, hint: '' };
}

/**
 * Decide a Done-as-SUBMIT on the OPTIONAL PHONE field (X1). Phone is never REQUIRED and never
 * rejected. Empty → 'skip' (nothing to submit; advance per the email state). Non-empty + changed →
 * 'submit'; non-empty + unchanged → 'resubmit'. The advance mirrors R31: with a valid email the phone
 * leg completes the contact → 'send'; without → 'surface-send' (nudge the email; never blind-send).
 *
 * @param value          the current phone value (verbatim; trimmed inside).
 * @param opts.emailValid  is the email already a valid shape? (gates 'send' vs 'surface-send').
 * @param opts.lastSubmitted the value the CURRENT phone ack describes (idempotent re-ack when equal).
 */
export function decidePhoneSubmit(
  value: string,
  opts: { emailValid: boolean; lastSubmitted: string }
): SubmitDecision {
  const v = value.trim();
  const advance: SubmitDecision['advance'] = opts.emailValid ? 'send' : 'surface-send';
  if (v === '') {
    // Optional + empty → not an error, nothing to post. Advance per the email state.
    return { action: 'skip', advance, hint: '' };
  }
  if (v === opts.lastSubmitted.trim() && opts.lastSubmitted.trim() !== '') {
    return { action: 'resubmit', advance, hint: '' };
  }
  return { action: 'submit', advance, hint: '' };
}

/**
 * X2 — reconcile a field's persistent ack against the CURRENT value (the truth check). The ack is
 * only truthful while the box still holds the value it describes; any change drops back to 'idle'.
 *
 * Returns the record UNCHANGED when the current value still matches `submittedValue` (Saved HOLDS —
 * the whole point of X2); returns a fresh IDLE record when the value has drifted (the visitor edited
 * the box, so a stale Saved would be a lie). Pure — the host calls it on every value change.
 */
export function reconcileSubmitAck(record: FieldSubmit, currentValue: string): FieldSubmit {
  if (record.state === 'idle') return record;
  if (currentValue.trim() === record.submittedValue.trim()) return record; // still truthful — HOLD.
  return IDLE_SUBMIT; // value drifted — the ack no longer describes what's in the box.
}

/**
 * X2 — advance a field's ack on a server `ui.contact_ack`. The server ack is session-wide (it does
 * not name the field), so the host applies it to the field(s) currently 'pending' at the submitted
 * value. `persisted:true` → 'saved'; a rejection / error state → 'error'. A 'saved'/'error'/'idle'
 * record is left alone (only a live 'pending' submit resolves on an ack). Pure.
 *
 * @param record    the field's current submit record.
 * @param persisted the server's persisted verdict (true = on file → Saved; false/undefined depends on
 *                  `treatUnpersistedAsError`). For the contact-only Done submit, `persisted:false`
 *                  from the send path means "queued/deferred" NOT "failed" — but for a CONTACT submit
 *                  the lead row is written regardless of send delivery, so we treat a non-error ack as
 *                  Saved. An explicit error state maps to 'error'.
 */
export function resolveSubmitAck(
  record: FieldSubmit,
  ack: { persisted: boolean; error?: boolean }
): FieldSubmit {
  if (record.state !== 'pending') return record; // only a live submit resolves.
  if (ack.error) return { state: 'error', submittedValue: record.submittedValue };
  // Contact persistence (the lead row) is independent of spec-sheet SEND delivery: `persisted:false`
  // here means "send deferred", not "contact lost" — the visitor's email IS on file. So any non-error
  // ack for a contact submit is a truthful Saved. (The send-truth lives in the separate SendState.)
  return { state: 'saved', submittedValue: record.submittedValue };
}

/**
 * X2 — the SavedLabel copy for a state (kept out of the component so it's one source of truth + the
 * test can assert the exact strings). Never silence: even 'error' has a human, actionable label.
 */
export function submitAckLabel(state: SubmitAckState): string {
  switch (state) {
    case 'saved':
      return 'Saved';
    case 'pending':
      return 'Saving…';
    case 'error':
      return 'Tap to retry';
    default:
      return 'Done';
  }
}
